import React from 'react';

import { fg } from '@confluence/feature-gating';
import type { ReplyData } from '@confluence/comments-data/entry-points/useCommentsData';
import { ViewValues } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData';

export const DEFAULT_NUM_VISIBLE_REPLIES = 2;

export enum ViewReplyOptions {
	ALL = 'ALL', // shows all replies
	DEFAULT = 'DEFAULT', // shows up to 3 unread comments
	HIDDEN = 'HIDDEN',
	LIMITED = 'LIMITED',
}

export type UpdateVisibleRepliesProps = {
	currentReplyView: ViewReplyOptions;
	setVisibleReplies: React.Dispatch<React.SetStateAction<ReplyData[]>>;
	allReplies: ReplyData[];
	isParentCommentOpen: boolean;
	latestReplies: ReplyData[];
	parentCommentId: string;
	currentView?: ViewValues;
	allowNestedReplies?: boolean;
};

export const legacyUpdateVisibleReplies = ({
	allReplies,
	commentType,
	isParentCommentOpen,
	currentReplyView,
	latestReplies,
	latestReplyIds,
	parentCommentId,
	setVisibleReplies,
	shouldShowLatestReplies,
	numOfHiddenReplies,
}: LegacyUpdateVisibleRepliesProps) => {
	if (currentReplyView === ViewReplyOptions.ALL) {
		if (commentType !== CommentType.GENERAL) {
			setVisibleReplies(allReplies);
		} else {
			const topLevelReplies = allReplies.filter((reply) => reply.parentId === parentCommentId);
			setVisibleReplies(topLevelReplies);
		}
		numOfHiddenReplies.current = 0;
	} else {
		numOfHiddenReplies.current = allReplies.length;
		if (!isParentCommentOpen) {
			// If the parent comment is resolved, we hide all replies on the thread's initial load
			setVisibleReplies([]);
		} else if (
			shouldShowLatestReplies &&
			latestReplies &&
			latestReplyIds &&
			latestReplyIds.size > 0
		) {
			/*
			 * If the parent comment is open, we show the latest replies on the thread's initial load only.
			 * When replies are expaneded and collapsed, all replies are hidden.
			 */
			setVisibleReplies(latestReplies);
			numOfHiddenReplies.current -= latestReplyIds.size;
		}
	}
};

export const updateVisibleReplies = ({
	currentReplyView,
	setVisibleReplies,
	allReplies,
	isParentCommentOpen,
	latestReplies,
	parentCommentId,
	currentView,
	allowNestedReplies = false,
}: UpdateVisibleRepliesProps) => {
	if (!isParentCommentOpen && currentView !== ViewValues.RESOLVED) {
		setVisibleReplies([]);
		return;
	}

	switch (currentReplyView) {
		case ViewReplyOptions.ALL:
			if (allowNestedReplies) {
				setVisibleReplies(allReplies);
			} else {
				const topLevelReplies = getCommentReplies(allReplies, parentCommentId);
				// Make all direct replies to the parent comment visible, excluding any nested replies.
				setVisibleReplies(topLevelReplies);
			}
			break;
		case ViewReplyOptions.HIDDEN:
			setVisibleReplies([]);
			break;
		case ViewReplyOptions.LIMITED:
			setVisibleReplies(latestReplies);
			break;
	}
};

export const getCommentReplies = (replies: ReplyData[], parentCommentId: string) =>
	replies.filter(({ parentId }) => parentId === parentCommentId);

export type LegacyUpdateVisibleRepliesProps = UpdateVisibleRepliesProps & {
	allReplies: ReplyData[];
	commentType: CommentType;
	isParentCommentOpen: boolean;
	currentReplyView: ViewReplyOptions;
	latestReplies: ReplyData[];
	latestReplyIds: Set<string>;
	parentCommentId: string;
	setVisibleReplies: React.Dispatch<React.SetStateAction<ReplyData[]>>;
	shouldShowLatestReplies: boolean;
	numOfHiddenReplies: React.MutableRefObject<number>;
};

type GetNumOfHiddenRepliesProps = {
	replies: ReplyData[];
	latestReplyIds: Set<string>;
	currentReplyView: ViewReplyOptions;
};

export const getNumOfHiddenReplies = ({
	replies,
	latestReplyIds,
	currentReplyView,
}: GetNumOfHiddenRepliesProps) => {
	switch (currentReplyView) {
		case ViewReplyOptions.HIDDEN:
			return replies.length;
		case ViewReplyOptions.LIMITED:
			return replies.length - latestReplyIds.size;
		default:
			return 0;
	}
};

export const getDefaultHiddenReplies = ({
	replies,
	shouldShowLatestReplies,
	isParentOpen,
	latestReplyIds,
}: {
	replies: ReplyData[];
	shouldShowLatestReplies: boolean;
	isParentOpen: boolean;
	latestReplyIds: Set<string>;
}) =>
	replies.filter((reply) => {
		if (fg('confluence_frontend_new_dangling_comments_ux')) {
			return !shouldShowLatestReplies || !latestReplyIds.has(reply.id);
		}

		return !shouldShowLatestReplies || !isParentOpen ? true : !latestReplyIds.has(reply.id);
	});

export type ProfilePictureInfo = {
	displayName: string;
	profilePicturePath: string | undefined;
};

// filter out duplicate authors when we're showing the profile pictures
export const getProfilePicturesOfHiddenReplies = (
	repliesToFilter: ReplyData[],
): ProfilePictureInfo[] => {
	if (!fg('confluence_frontend_comments_panel_v2')) {
		return [];
	}

	const uniqueAccountsMap = new Map<string, ProfilePictureInfo>();
	// Start from the end of the list and move backwards
	for (let i = repliesToFilter.length - 1; i >= 0; i--) {
		const reply = repliesToFilter[i];
		const { displayName, profilePicture } = reply.author;
		// Determine if the author has an accountId, anonymous users don't have accountId
		let uniqueKey: string;
		if ('accountId' in reply.author && reply.author.accountId) {
			uniqueKey = reply.author.accountId;
		} else {
			// Fallback for anonymous users or if accountId is not present
			uniqueKey = `anonymous-${i}`;
		}
		if (!uniqueAccountsMap.has(uniqueKey)) {
			uniqueAccountsMap.set(uniqueKey, {
				displayName: displayName ?? '',
				profilePicturePath: profilePicture?.path,
			});
		}
	}
	return Array.from(uniqueAccountsMap.values());
};
